/**
 * Subscription guard metrics
 *
 * Lightweight in-memory + sessionStorage counters to measure:
 * - How many times the SubscriptionGuard redirected the user to /app/subscription
 *   (and whether that redirect was likely "spurious" — i.e. happened right after
 *   a wake/revalidation event)
 * - How many revalidations were triggered by visibilitychange / online / interval / auth
 *
 * Counters are exposed via:
 *  - console logs (info level)
 *  - PostHog events (when available)
 *  - window.__subscriptionMetrics for manual inspection in DevTools
 *  - sessionStorage key 'careos_sub_metrics' (survives soft reloads in same tab)
 */
import { captureEvent } from '@/lib/posthog';

const STORAGE_KEY = 'careos_sub_metrics';

export type RevalidationTrigger =
  | 'visibilitychange'
  | 'online'
  | 'interval'
  | 'session-change'
  | 'manual'
  | 'retry';

export type RedirectReason =
  | 'no-subscription'
  | 'usage-exceeded'
  | 'unknown';

interface MetricsSnapshot {
  // Revalidations grouped by trigger
  revalidations: Record<RevalidationTrigger, number>;
  // Total revalidations (sum of all triggers)
  revalidationsTotal: number;
  // Redirects to /app/subscription
  redirects: number;
  // Subset of `redirects` that happened within the post-wake grace window
  // (likely spurious — should be ~0 with current defenses)
  spuriousRedirects: number;
  // Last time something happened, useful for debugging timing windows
  lastRevalidationAt: number | null;
  lastRedirectAt: number | null;
  // Recent revalidations (timestamp + trigger) — capped for memory
  recentRevalidations: Array<{ ts: number; trigger: RevalidationTrigger }>;
}

const SPURIOUS_WINDOW_MS = 10_000;
const MAX_RECENT = 20;

function emptySnapshot(): MetricsSnapshot {
  return {
    revalidations: {
      visibilitychange: 0,
      online: 0,
      interval: 0,
      'session-change': 0,
      manual: 0,
      retry: 0,
    },
    revalidationsTotal: 0,
    redirects: 0,
    spuriousRedirects: 0,
    lastRevalidationAt: null,
    lastRedirectAt: null,
    recentRevalidations: [],
  };
}

function load(): MetricsSnapshot {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return emptySnapshot();
    const parsed = JSON.parse(raw);
    return { ...emptySnapshot(), ...parsed, revalidations: { ...emptySnapshot().revalidations, ...(parsed.revalidations ?? {}) } };
  } catch {
    return emptySnapshot();
  }
}

function persist(snapshot: MetricsSnapshot) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // sessionStorage may be unavailable (SSR, private mode) — silent fail
  }
  if (typeof window !== 'undefined') {
    (window as any).__subscriptionMetrics = snapshot;
  }
}

let snapshot: MetricsSnapshot = typeof window !== 'undefined' ? load() : emptySnapshot();
if (typeof window !== 'undefined') {
  (window as any).__subscriptionMetrics = snapshot;
}

function track(event: string, props: Record<string, unknown>) {
  try {
    captureEvent(event, props);
  } catch {
    // analytics is best-effort
  }
}

export function recordRevalidation(trigger: RevalidationTrigger) {
  const now = Date.now();
  snapshot.revalidations[trigger] = (snapshot.revalidations[trigger] ?? 0) + 1;
  snapshot.revalidationsTotal += 1;
  snapshot.lastRevalidationAt = now;
  snapshot.recentRevalidations.push({ ts: now, trigger });
  if (snapshot.recentRevalidations.length > MAX_RECENT) {
    snapshot.recentRevalidations.splice(0, snapshot.recentRevalidations.length - MAX_RECENT);
  }
  persist(snapshot);

  console.info('[SubMetrics] revalidation', {
    trigger,
    total: snapshot.revalidationsTotal,
    byTrigger: snapshot.revalidations[trigger],
  });
  track('subscription_revalidation', {
    trigger,
    total: snapshot.revalidationsTotal,
    by_trigger_count: snapshot.revalidations[trigger],
  });
}

export function recordRedirect(reason: RedirectReason, context: Record<string, unknown> = {}) {
  const now = Date.now();
  snapshot.redirects += 1;
  snapshot.lastRedirectAt = now;

  const sinceLastReval = snapshot.lastRevalidationAt
    ? now - snapshot.lastRevalidationAt
    : null;
  const isSpurious = sinceLastReval !== null && sinceLastReval < SPURIOUS_WINDOW_MS;
  if (isSpurious) {
    snapshot.spuriousRedirects += 1;
  }
  persist(snapshot);

  const payload = {
    reason,
    total_redirects: snapshot.redirects,
    spurious_redirects: snapshot.spuriousRedirects,
    ms_since_last_revalidation: sinceLastReval,
    likely_spurious: isSpurious,
    ...context,
  };

  if (isSpurious) {
    console.warn('[SubMetrics] redirect to /app/subscription (LIKELY SPURIOUS)', payload);
  } else {
    console.info('[SubMetrics] redirect to /app/subscription', payload);
  }
  track('subscription_redirect', payload);
}

export function getSubscriptionMetrics(): MetricsSnapshot {
  return { ...snapshot, revalidations: { ...snapshot.revalidations }, recentRevalidations: [...snapshot.recentRevalidations] };
}

export function resetSubscriptionMetrics() {
  snapshot = emptySnapshot();
  persist(snapshot);
}
